import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  mockClaimReward,
  mockGetMarketingSavings,
  mockGetRewardDetail,
  mockGetRewards,
  mockGetSavingsSummary,
  mockGetSubscriptionPlan,
  mockGetUserAddress,
  mockUpdateUserAddress,
} from "../../../(general-header)/botb-rewards/rewards-mocks";
import type {
  AddressDetailResponse,
  ClaimResponse,
  ClaimRewardRequest,
  MarketingSavingsResponse,
  PostcodeLookupResult,
  RewardDetailResponse,
  RewardsListResponse,
  SavingsSummaryResponse,
  UserAddressRequest,
  UserAddressResponse,
} from "../../../(general-header)/botb-rewards/types";

const USE_MOCKS = false;

const subscriptionBaseQuery = fetchBaseQuery({
  baseUrl: process.env.NEXT_PUBLIC_SUBSCRIPTION_HOST,
  credentials: "include",
});

export const rewardsApi = createApi({
  reducerPath: "rewardsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.NEXT_PUBLIC_HOST,
    credentials: "include",
  }),
  tagTypes: ["Rewards", "RewardDetail", "Savings", "Subscription", "UserAddress"],
  endpoints: build => ({
    getSubscriptionPlan: build.query<SubscriptionPlanUser, void>({
      queryFn: async (_arg, api, extraOptions) => {
        if (USE_MOCKS) return mockGetSubscriptionPlan();
        const result = await subscriptionBaseQuery("/api/subscriptions/getSubscriptionPlan", api, extraOptions);
        if (result.error) return { error: result.error };
        return { data: result.data as SubscriptionPlanUser };
      },
      providesTags: ["Subscription"],
    }),

    getRewards: build.query<RewardsListResponse, void>({
      queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
        if (USE_MOCKS) return mockGetRewards();
        const result = await baseQuery("/umbraco/BotB/Pass/Rewards");
        if (result.error) return { error: result.error };
        return { data: result.data as RewardsListResponse };
      },
      providesTags: ["Rewards"],
    }),

    getRewardDetail: build.query<RewardDetailResponse, string>({
      queryFn: async (rewardId, _api, _extraOptions, baseQuery) => {
        if (USE_MOCKS) return mockGetRewardDetail(rewardId);
        const result = await baseQuery(`/umbraco/BotB/Pass/Reward?id=${rewardId}`);
        if (result.error) return { error: result.error };
        return { data: result.data as RewardDetailResponse };
      },
      providesTags: (_result, _error, rewardId) => [{ type: "RewardDetail", id: rewardId }],
    }),

    getSavingsSummary: build.query<SavingsSummaryResponse, void>({
      queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
        if (USE_MOCKS) return mockGetSavingsSummary();
        const result = await baseQuery("/umbraco/BotB/Pass/SavingsSummary");
        if (result.error) return { error: result.error };
        return { data: result.data as SavingsSummaryResponse };
      },
      providesTags: ["Savings"],
    }),

    getMarketingSavings: build.query<MarketingSavingsResponse, void>({
      queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
        if (USE_MOCKS) return mockGetMarketingSavings();
        const result = await baseQuery("/umbraco/BotB/Pass/MarketingSavings");
        if (result.error) return { error: result.error };
        return { data: result.data as MarketingSavingsResponse };
      },
    }),

    claimReward: build.mutation<ClaimResponse, { rewardId: string; body?: ClaimRewardRequest }>({
      queryFn: async ({ rewardId, body }, _api, _extraOptions, baseQuery) => {
        if (USE_MOCKS) return mockClaimReward(rewardId);
        const result = await baseQuery({
          url: `/umbraco/BotB/Pass/ClaimReward?id=${rewardId}`,
          method: "POST",
          body: body ?? {},
        });
        if (result.error) return { error: result.error };
        return { data: result.data as ClaimResponse };
      },
      invalidatesTags: (_result, _error, { rewardId }) => [
        "Rewards",
        "Savings",
        { type: "RewardDetail", id: rewardId },
      ],
    }),

    getUserAddress: build.query<UserAddressResponse, void>({
      queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
        if (USE_MOCKS) return mockGetUserAddress();
        const result = await baseQuery("/umbraco/BotB/Pass/UserAddress");
        if (result.error) return { error: result.error };
        return { data: result.data as UserAddressResponse };
      },
      providesTags: ["UserAddress"],
    }),

    updateUserAddress: build.mutation<UserAddressResponse, UserAddressRequest>({
      queryFn: async (body, _api, _extraOptions, baseQuery) => {
        if (USE_MOCKS) return mockUpdateUserAddress();
        const result = await baseQuery({
          url: "/umbraco/BotB/Pass/UserAddress",
          method: "POST",
          body,
        });
        if (result.error) return { error: result.error };
        return { data: result.data as UserAddressResponse };
      },
      invalidatesTags: ["UserAddress"],
    }),

    lookupPostcode: build.query<PostcodeLookupResult[], string>({
      queryFn: async (postcode, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery(
          `/umbraco/botb/AddressLookupWeb/LookupUKPostcode?postcode=${encodeURIComponent(postcode)}`
        );
        if (result.error) return { error: result.error };
        return { data: result.data as PostcodeLookupResult[] };
      },
    }),

    getAddressDetails: build.query<AddressDetailResponse, string>({
      queryFn: async (addressId, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery(
          `/umbraco/botb/AddressLookupWeb/GetUKAddressDetailsById?addressId=${encodeURIComponent(addressId)}`
        );
        if (result.error) return { error: result.error };
        return { data: result.data as AddressDetailResponse };
      },
    }),
  }),
});

export const {
  useGetSubscriptionPlanQuery,
  useGetRewardsQuery,
  useGetRewardDetailQuery,
  useGetSavingsSummaryQuery,
  useGetMarketingSavingsQuery,
  useClaimRewardMutation,
  useGetUserAddressQuery,
  useUpdateUserAddressMutation,
  useLazyLookupPostcodeQuery,
  useLazyGetAddressDetailsQuery,
} = rewardsApi;
